<template>
  <div class="institution-logs-container">
    <b-card class="logs-card">
      <!-- Header -->
      <div class="logs-header">
        <div class="header-content">
          <h4 class="logs-title">
            <b-icon-clock-history class="title-icon"></b-icon-clock-history>
            {{ $t('toolbar.adminUsers.institutionLogs') }}
          </h4>
          <p class="logs-subtitle">
            {{ $t('toolbar.adminUsers.institutionLogsSubtitle') }}
          </p>
        </div>
        <div class="header-actions">
          <b-button
            variant="outline-primary"
            size="sm"
            @click="fetchLogs"
            :disabled="loading"
          >
            <b-icon-arrow-clockwise
              :animation="loading ? 'spin' : ''"
            ></b-icon-arrow-clockwise>
            {{ $t('toolbar.adminUsers.update') }}
          </b-button>
        </div>
      </div>

      <!-- Tabla de Logs -->
      <div class="table-responsive">
        <NewGenericBTable
          :key="generic_table_key"
          :fields="fields"
          :search_filter="false"
          :columns_display="false"
          :pre_current_page="current_page"
          :pre_filter_tags="filterTags"
          @current_page="currentPage"
          :url="'/common/audit-log/'"
          :sticky-header="'600px'"
        >
          <template #external-search>
            <!-- Filtros -->
            <div class="filters-section">
              <div class="filters-grid">
                <div class="filter-item">
                  <b-form-group
                    :label="$t('reusable.common.search')"
                    label-for="search-input"
                    class="filter-group"
                  >
                    <div class="input-icon-wrapper">
                      <b-icon-search class="input-icon"></b-icon-search>
                      <b-form-input
                        id="search-input"
                        v-model="searchTerm"
                        :placeholder="$t('toolbar.adminUsers.searchPlaceholder')"
                        debounce="500"
                        class="input-with-icon"
                      ></b-form-input>
                    </div>
                  </b-form-group>
                </div>
                <div class="filter-item">
                  <b-form-group
                    :label="$t('toolbar.adminUsers.date')"
                    label-for="date-filter"
                    class="filter-group"
                  >
                    <div class="d-flex align-items-center gap-2">
                      <div class="input-icon-wrapper flex-grow-1">
                        <b-icon-calendar3 class="input-icon"></b-icon-calendar3>
                        <b-form-input
                          id="date-filter"
                          v-model="dateFilter"
                          type="date"
                          class="input-with-icon"
                        ></b-form-input>
                      </div>
                      <b-button
                        v-if="dateFilter != ''"
                        variant="none"
                        size="sm"
                        v-b-tooltip.v-secondary.noninteractive="$t('toolbar.adminUsers.clearDate')"
                        @click="dateFilter = ''"
                      >
                        <b-icon-trash variant="danger"></b-icon-trash>
                      </b-button>
                    </div>
                  </b-form-group>
                </div>
                <div class="filter-item">
                  <b-form-group
                    :label="$t('toolbar.adminUsers.actionType')"
                    label-for="action-filter"
                    class="filter-group"
                  >
                    <div class="input-icon-wrapper">
                      <b-icon-funnel class="input-icon"></b-icon-funnel>
                      <b-form-select
                        id="action-filter"
                        v-model="actionFilter"
                        :options="actionOptions"
                        class="select-with-icon"
                      ></b-form-select>
                    </div>
                  </b-form-group>
                </div>
              </div>
            </div>
          </template>
          <template #cell(created_at)="row">
            <div class="timestamp-cell">
              <b-icon-calendar3 class="mr-1"></b-icon-calendar3>
              {{ row.item.created_at | FormatToDateTime }}
            </div>
          </template>

          <template #cell(action)="row">
            <b-badge :variant="getActionVariant(row.item.action)">
              {{ row.item.action }}
            </b-badge>
          </template>

          <template #cell(user_id)="row">
            <div class="user-cell">
              <b-icon-person-circle class="mr-1"></b-icon-person-circle>
              {{ getUserName(row.item) }}
            </div>
          </template>
          <template #cell(user_email)="row">
            <div class="user-cell">
              <b-icon-envelope class="mr-1"></b-icon-envelope>
              {{ row.item.user_email }}
            </div>
          </template>

          <template #cell(model_alternative_name)="row">
            <div class="model-cell">
              <b-icon-file-earmark-code class="mr-1"></b-icon-file-earmark-code>
              <AlternativeNameButton
                :modal_ids="`${row.item.id}`"
                :table_name="`${
                  row.item.app_name
                }.${row.item.model_name.toLowerCase()}`"
                :allows_crud="true"
                :show_button="false"
                :text_default="`${
                  row.item.model_alternative_name == '' ||
                  row.item.model_alternative_name == null
                    ? row.item.model_name
                    : row.item.model_alternative_name
                }`"
              ></AlternativeNameButton>
            </div>
          </template>

          <template #cell(ip_address)="row">
            <div class="ip-cell">
              <b-icon-globe class="mr-1"></b-icon-globe>
              {{ row.item.ip_address || $t('reusable.common.notApplicable') }}
            </div>
          </template>

          <template #cell(details)="row">
            <b-button
              v-if="
                row.item &&
                (row.item.after_state != null || row.item.before_state != null)
              "
              variant="link"
              size="sm"
              @click="showDetails(row.item)"
              class="details-button"
            >
              <b-icon-eye></b-icon-eye>
              {{ $t('toolbar.adminUsers.viewDetails') }}
            </b-button>
          </template>
        </NewGenericBTable>
      </div>
    </b-card>

    <!-- Modal de Detalles -->
    <b-modal
      id="log-details-modal"
      :title="$t('toolbar.adminUsers.logDetails')"
      size="xl"
      hide-footer
      modal-class="log-details-modal"
    >
      <div v-if="selectedLog" class="log-details">
        <!-- Información General -->
        <div class="info-header">
          <div class="info-item">
            <b-icon-calendar3 class="info-icon"></b-icon-calendar3>
            <div>
              <small class="info-label">{{ $t('toolbar.adminUsers.dateAndTime') }}</small>
              <p class="info-value">
                {{ selectedLog.created_at | FormatToDateTime }}
              </p>
            </div>
          </div>
          <div class="info-item">
            <b-icon-person-circle class="info-icon"></b-icon-person-circle>
            <div>
              <small class="info-label">{{ $t('toolbar.adminUsers.user') }}</small>
              <p class="info-value">{{ getUserName(selectedLog) }}</p>
            </div>
          </div>
          <div class="info-item">
            <b-icon-file-earmark-code
              class="info-icon"
            ></b-icon-file-earmark-code>
            <div>
              <small class="info-label">{{ $t('toolbar.adminUsers.model') }}</small>
              <p class="info-value">
                {{
                  selectedLog.model_alternative_name == "" ||
                  selectedLog.model_alternative_name == null
                    ? selectedLog.model_name
                    : selectedLog.model_alternative_name
                }}
              </p>
            </div>
          </div>
          <div class="info-item">
            <b-icon-tag class="info-icon"></b-icon-tag>
            <div>
              <small class="info-label">{{ $t('toolbar.adminUsers.action') }}</small>
              <p class="info-value">
                <b-badge :variant="getActionVariant(selectedLog.action)">
                  {{ getActionLabel(selectedLog.action) }}
                </b-badge>
              </p>
            </div>
          </div>
          <div class="info-item">
            <b-icon-globe class="info-icon"></b-icon-globe>
            <div>
              <small class="info-label">{{ $t('toolbar.adminUsers.ip') }}</small>
              <p class="info-value">{{ selectedLog.ip_address || $t('reusable.common.notApplicable') }}</p>
            </div>
          </div>
        </div>

        <!-- Comparación de Estados -->
        <div class="states-comparison">
          <!-- Vista unificada en una sola tarjeta -->
          <div class="unified-card">
            <!-- UPDATE: Mostrar todos los campos con indicador de cambio -->
            <div v-if="selectedLog.action === 'UPDATE'">
              <div
                v-for="field in getAllFieldsWithChanges(
                  selectedLog.before_state,
                  selectedLog.after_state
                )"
                :key="field.key"
                class="field-row"
                :class="{ 'field-changed': field.changed }"
              >
                <div class="field-info">
                  <div class="field-label">
                    <b-icon-pencil-square
                      v-if="field.changed"
                      class="change-icon mr-2"
                      variant="warning"
                    ></b-icon-pencil-square>
                    <b-icon-circle-fill
                      v-else
                      class="unchanged-icon mr-2"
                    ></b-icon-circle-fill>
                    <strong>{{ field.key }}</strong>
                  </div>
                  <div v-if="field.changed" class="field-values">
                    <div class="value-comparison">
                      <div class="old-value">
                        <small class="value-label">{{ $t('toolbar.adminUsers.previous') }}</small>
                        <RenderVHtml
                          v-if="isHtml(field.before)"
                          class="value-text html-content"
                          :content="field.before"
                        ></RenderVHtml>
                        <span v-else class="value-text">{{
                          formatValue(field.before)
                        }}</span>
                      </div>
                      <b-icon-arrow-right
                        class="arrow-icon"
                      ></b-icon-arrow-right>
                      <div class="new-value">
                        <small class="value-label">{{ $t('toolbar.adminUsers.new') }}</small>
                        <RenderVHtml
                          v-if="isHtml(field.after)"
                          class="value-text html-content"
                          :content="field.after"
                        ></RenderVHtml>
                        <span v-else class="value-text">{{
                          formatValue(field.after)
                        }}</span>
                      </div>
                    </div>
                  </div>
                  <div v-else class="field-value-unchanged">
                    <RenderVHtml
                      v-if="isHtml(field.after)"
                      class="value-text html-content"
                      :content="field.after"
                    ></RenderVHtml>
                    <span v-else class="value-text">{{
                      formatValue(field.after)
                    }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- CREATE: Mostrar en una sola tarjeta -->
            <div v-else-if="selectedLog.action === 'CREATE'">
              <div class="action-header create-header">
                <b-icon-plus-circle class="mr-2"></b-icon-plus-circle>
                <h5>{{ $t('toolbar.adminUsers.createdObject') }}</h5>
              </div>
              <div
                v-for="(value, key) in filterJSONData(selectedLog.after_state)"
                :key="key"
                class="field-row"
              >
                <div class="field-info">
                  <div class="field-label">
                    <strong>{{ key }}</strong>
                  </div>
                  <div class="field-value-single">
                    <RenderVHtml
                      v-if="isHtml(value)"
                      class="value-text html-content"
                      :content="value"
                    ></RenderVHtml>
                    <span v-else class="value-text">{{ formatValue(value) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- DELETE: Mostrar en una sola tarjeta -->
            <div v-else-if="selectedLog.action === 'DELETE'">
              <div class="action-header delete-header">
                <b-icon-trash class="mr-2"></b-icon-trash>
                <h5>{{ $t('toolbar.adminUsers.deletedObject') }}</h5>
              </div>
              <div
                v-for="(value, key) in filterJSONData(selectedLog.before_state)"
                :key="key"
                class="field-row"
              >
                <div class="field-info">
                  <div class="field-label">
                    <strong>{{ key }}</strong>
                  </div>
                  <div class="field-value-single deleted-value">
                    <RenderVHtml
                      v-if="isHtml(value)"
                      class="value-text html-content"
                      :content="value"
                    ></RenderVHtml>
                    <span v-else class="value-text">{{ formatValue(value) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
// import * as names from "@/store/names";
// import { mapGetters } from "vuex";
export default {
  name: "InstitutionLogsView",
  components: {
    NewGenericBTable: () => import("@/components/reusable/NewGenericBTable"),
    AlternativeNameButton: () =>
      import("@/components/reusable/AlternativeNameButton"),
    RenderVHtml: () => import("@/components/reusable/RenderVHtml"),
  },
  data() {
    return {
      generic_table_key: 0,
      current_page: 1,
      loading: false,
      searchTerm: "",
      actionFilter: null,
      dateFilter: "",
      selectedLog: null,
      actionOptions: [
        { value: null, text: this.$t("toolbar.adminUsers.allActions") },
        { value: "CREATE", text: this.$t("toolbar.adminUsers.creation") },
        { value: "UPDATE", text: this.$t("toolbar.adminUsers.updateAction") },
        { value: "DELETE", text: this.$t("toolbar.adminUsers.deletion") },
      ],
      fields: [
        {
          key: "user_id",
          label: this.$t("toolbar.adminUsers.user"),
          sortable: true,
          display_column: true,
        },
        {
          key: "user_email",
          label: this.$t("toolbar.adminUsers.email"),
          sortable: true,
          display_column: true,
        },
        {
          key: "model_alternative_name",
          label: this.$t("toolbar.adminUsers.model"),
          sortable: true,
          display_column: true,
        },
        {
          key: "created_at",
          label: this.$t("toolbar.adminUsers.dateAndTime"),
          sortable: true,
          display_column: true,
        },
        {
          key: "action",
          label: this.$t("toolbar.adminUsers.action"),
          sortable: true,
          display_column: true,
        },
        {
          key: "ip_address",
          label: this.$t("toolbar.adminUsers.ip"),
          sortable: true,
          display_column: true,
        },
        {
          key: "details",
          label: "",
          sortable: false,
          display_column: true,
        },
      ],
    };
  },
  computed: {
    // ...mapGetters({
    //   users: names.USERS,
    // }),
    filterTags() {
      const tags = [];

      // Búsqueda general - busca en usuario, email y modelo (OR)
      if (this.searchTerm) {
        tags.push(`search=${this.searchTerm}`);
      }

      // Filtro de acción
      if (this.actionFilter) {
        tags.push(`action=${this.actionFilter}`);
      }

      // Filtro de fecha
      if (this.dateFilter) {
        tags.push(`created_at__date=${this.dateFilter}`);
      }

      return tags;
    },
  },
  methods: {
    fetchLogs() {
      this.generic_table_key++;
    },
    currentPage(current_page) {
      this.current_page = current_page;
    },
    getUserName(row) {
      return `${row.user_first_name} ${row.user_last_name}`.trim();
    },
    filterJSONData(data) {
      // Campos relevantes a mostrar (whitelist)
      const relevantFields = [
        "name",
        "title",
        "full_name",
        "description",
        "text",
        "comment",
        "comments",
        "sentence",
        "full_sentence",
        "action",
        "verb",
        "content",
        "condition",
        "order",
        "weighing",
        "score",
        "email",
        "first_name",
        "last_name",
        "professional_title",
        "academic_degree",
        "code",
        "identifier",
        "identifier_type",
        "start_date",
        "end_date",
        "start_time",
        "end_time",
        "init_date",
        "publish_date",
        "due_date",
        "compliance_date",
        "color",
        "credits",
        "institutional_credits",
        "hours",
        "weeks",
        "time",
        "min_grade",
        "max_grade",
        "appr_grade",
        "exigence_percentage",
        "visible_by_evaluatees",
        "active",
        "publish",
        "type",
        "category",
        "status",
        "sub_type",
        "instrument_type",
        "semester_amount",
        "cohort",
        "agreement",
        "responsibles",
        "observations",
        "max_score",
        "max_answer_length",
      ];
      // Campos técnicos/metadata a excluir (blacklist)
      const excludePatterns = [
        /^id$/,
        /_id$/,
        /_ids$/,
        /^creation_date$/,
        /^update_date$/,
        /^created_by$/,
        /^updated_by$/,
        /^school$/,
        /^assessment$/,
        /^assessment_name$/,
        /^is_/,
        /^has_/,
        /^can_/,
        /^allow_/,
        /^show_/,
        /^use_/,
        /_locked/,
        /^automated_/,
        /^internal_use_id$/,
        /_oid$/,
        /^sso_/,
        /^must_/,
        /^azure_/,
        /^evaluation/,
        /^is_batch_loaded$/,
        /^is_replicated$/,
      ];
      const filterData = (obj) => {
        if (obj === null || obj === undefined) return obj;

        if (Array.isArray(obj)) {
          return obj.map((item) => filterData(item));
        }

        if (typeof obj === "object") {
          const filtered = {};

          for (const key in obj) {
            // Excluir si coincide con algún patrón de blacklist
            const shouldExclude = excludePatterns.some((pattern) =>
              pattern.test(key)
            );

            // Incluir si está en la whitelist o si no está en la blacklist
            if (relevantFields.includes(key) || !shouldExclude) {
              // Verificar que no sea un campo que termine en _id o _ids
              if (!/_ids?$/.test(key) || relevantFields.includes(key)) {
                filtered[key] = filterData(obj[key]);
              }
            }
          }

          return filtered;
        }

        return obj;
      };

      return filterData(data);
    },
    // formatJSON(data) {
    //   if (!data) return "Sin datos";
    //   const filteredData = this.filterJSONData(data);
    //   return JSON.stringify(filteredData, null, 2);
    // },
    // formatJSONWithDiff(afterState, beforeState) {
    //   if (!afterState) return "Sin datos";

    //   const filteredAfter = this.filterJSONData(afterState);
    //   const filteredBefore = this.filterJSONData(beforeState);

    //   // Si no hay estado anterior (CREATE), mostrar sin resaltar
    //   if (!filteredBefore) {
    //     return this.escapeHtml(JSON.stringify(filteredAfter, null, 2));
    //   }

    //   // Función para detectar diferencias
    //   const getDifferences = (after, before, path = "") => {
    //     const differences = new Set();

    //     if (typeof after !== "object" || after === null) {
    //       if (after !== before) {
    //         differences.add(path);
    //       }
    //       return differences;
    //     }

    //     if (Array.isArray(after)) {
    //       if (
    //         !Array.isArray(before) ||
    //         JSON.stringify(after) !== JSON.stringify(before)
    //       ) {
    //         differences.add(path);
    //       }
    //       return differences;
    //     }

    //     // Comparar objetos
    //     const allKeys = new Set([
    //       ...Object.keys(after),
    //       ...Object.keys(before || {}),
    //     ]);
    //     for (const key of allKeys) {
    //       const newPath = path ? `${path}.${key}` : key;
    //       const afterValue = after[key];
    //       const beforeValue = before ? before[key] : undefined;

    //       if (JSON.stringify(afterValue) !== JSON.stringify(beforeValue)) {
    //         differences.add(newPath);
    //       }
    //     }

    //     return differences;
    //   };

    //   const differences = getDifferences(filteredAfter, filteredBefore);

    //   // Función para formatear JSON con resaltado de diferencias
    //   const formatWithHighlight = (obj, path = "", indent = 0) => {
    //     if (obj === null) return "null";
    //     if (obj === undefined) return "undefined";

    //     const indentStr = "  ".repeat(indent);
    //     const type = typeof obj;

    //     // Valores primitivos
    //     if (type === "boolean" || type === "number") {
    //       const isChanged = differences.has(path);
    //       return isChanged
    //         ? `<span class="json-changed">${obj}</span>`
    //         : String(obj);
    //     }

    //     if (type === "string") {
    //       const isChanged = differences.has(path);
    //       const escaped = `"${this.escapeHtml(obj)}"`;
    //       return isChanged
    //         ? `<span class="json-changed">${escaped}</span>`
    //         : escaped;
    //     }

    //     // Arrays
    //     if (Array.isArray(obj)) {
    //       if (obj.length === 0) return "[]";

    //       let result = "[\n";
    //       obj.forEach((item, index) => {
    //         const itemPath = `${path}[${index}]`;
    //         result += `${indentStr}  ${formatWithHighlight(
    //           item,
    //           itemPath,
    //           indent + 1
    //         )}`;
    //         if (index < obj.length - 1) result += ",";
    //         result += "\n";
    //       });
    //       result += `${indentStr}]`;
    //       return result;
    //     }

    //     // Objetos
    //     if (type === "object") {
    //       const keys = Object.keys(obj);
    //       if (keys.length === 0) return "{}";

    //       let result = "{\n";
    //       keys.forEach((key, index) => {
    //         const newPath = path ? `${path}.${key}` : key;
    //         const isChanged = differences.has(newPath);
    //         const keyDisplay = `"${this.escapeHtml(key)}"`;

    //         if (isChanged) {
    //           result += `${indentStr}  <span class="json-changed">${keyDisplay}: `;
    //           result += formatWithHighlight(obj[key], newPath, indent + 1);
    //           result += "</span>";
    //         } else {
    //           result += `${indentStr}  ${keyDisplay}: `;
    //           result += formatWithHighlight(obj[key], newPath, indent + 1);
    //         }

    //         if (index < keys.length - 1) result += ",";
    //         result += "\n";
    //       });
    //       result += `${indentStr}}`;
    //       return result;
    //     }

    //     return this.escapeHtml(String(obj));
    //   };

    //   return formatWithHighlight(filteredAfter);
    // },
    // escapeHtml(text) {
    //   const map = {
    //     "&": "&amp;",
    //     "<": "&lt;",
    //     ">": "&gt;",
    //     '"': "&quot;",
    //     "'": "&#039;",
    //   };
    //   return String(text).replace(/[&<>"']/g, (m) => map[m]);
    // },
    getActionLabel(action) {
      const labels = {
        CREATE: this.$t("toolbar.adminUsers.creation"),
        UPDATE: this.$t("toolbar.adminUsers.updateAction"),
        DELETE: this.$t("toolbar.adminUsers.deletion"),
      };
      return labels[action] || action;
    },
    getActionVariant(action) {
      const variants = {
        CREATE: "success",
        UPDATE: "primary",
        DELETE: "danger",
      };
      return variants[action] || "secondary";
    },
    showDetails(log) {
      this.selectedLog = log;
      this.$bvModal.show("log-details-modal");
    },
    getAllFieldsWithChanges(beforeState, afterState) {
      const filteredBefore = this.filterJSONData(beforeState) || {};
      const filteredAfter = this.filterJSONData(afterState) || {};

      const fields = [];
      const allKeys = new Set([
        ...Object.keys(filteredBefore),
        ...Object.keys(filteredAfter),
      ]);

      for (const key of allKeys) {
        const beforeValue = filteredBefore[key];
        const afterValue = filteredAfter[key];
        const changed =
          JSON.stringify(beforeValue) !== JSON.stringify(afterValue);

        fields.push({
          key: key,
          before: beforeValue,
          after: afterValue,
          changed: changed,
        });
      }

      // Ordenar: campos cambiados primero
      return fields.sort((a, b) => {
        if (a.changed && !b.changed) return -1;
        if (!a.changed && b.changed) return 1;
        return a.key.localeCompare(b.key);
      });
    },
    isHtml(value) {
      if (typeof value !== "string") return false;
      return /<[a-z][\s\S]*>/i.test(value);
    },
    formatValue(value) {
      if (value === null || value === undefined) {
        return this.$t("reusable.common.notApplicable");
      }

      if (typeof value === "object") {
        return JSON.stringify(value, null, 2);
      }

      if (typeof value === "boolean") {
        return value ? this.$t("reusable.common.yes") : this.$t("reusable.common.no");
      }

      return String(value);
    },
  },
  created() {},
};
</script>

<style scoped>
.institution-logs-container {
  padding: 1rem;
}

.logs-card {
  border: none;
  box-shadow: var(--kl-shadow-md);
  border-radius: var(--kl-radius-md);
}

.logs-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--kl-border-soft);
}

.header-content {
  flex: 1;
}

.logs-title {
  display: flex;
  align-items: center;
  font-family: var(--kl-font-family);
  font-size: 1.5rem;
  font-weight: var(--kl-fw-bold);
  color: var(--kl-text-heading);
  margin-bottom: 0.5rem;
}

.title-icon {
  font-size: 1.75rem;
  margin-right: 0.75rem;
  color: var(--kl-color-primary);
}

.logs-subtitle {
  color: var(--kl-text-muted);
  margin: 0;
  font-size: var(--kl-fs-body);
}

.header-actions {
  display: flex;
  gap: 0.5rem;
}

.filters-section {
  width: 100%;
  background: linear-gradient(
    135deg,
    var(--kl-surface-muted) 0%,
    var(--kl-border-soft) 100%
  );
  border-radius: var(--kl-radius-lg);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: var(--kl-shadow-sm);
  border: 1px solid var(--kl-border);
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  width: 100%;
}

.filter-item {
  width: 100%;
}

.filter-group {
  margin-bottom: 0;
}

.filter-group label {
  font-weight: var(--kl-fw-semibold);
  color: var(--kl-text-body);
  font-size: var(--kl-fs-caption);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: var(--kl-ls-wide);
}

.input-icon-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 12px;
  color: var(--kl-text-muted);
  z-index: 10;
  font-size: 1rem;
  pointer-events: none;
}

.input-with-icon,
.select-with-icon {
  padding-left: 38px !important;
  border-radius: var(--kl-input-radius);
  border: 1px solid var(--kl-input-border);
  transition: all 0.3s ease;
  background-color: var(--kl-surface);
  height: 42px;
  font-size: var(--kl-fs-body);
  width: 100%;
}

.input-with-icon:focus,
.select-with-icon:focus {
  border-color: var(--kl-input-border-focus);
  box-shadow: var(--kl-shadow-focus);
  background-color: var(--kl-surface);
}

.input-with-icon::placeholder {
  color: var(--kl-text-muted);
  font-style: italic;
}

.timestamp-cell,
.user-cell,
.model-cell,
.ip-cell {
  display: flex;
  align-items: center;
  color: var(--kl-text-body);
}

.model-cell {
  font-family: "Consolas", "Monaco", "Courier New", monospace;
  font-size: var(--kl-fs-body);
  font-weight: var(--kl-fw-medium);
}

.details-button {
  padding: 0.25rem 0.5rem;
  text-decoration: none;
}

.details-button:hover {
  text-decoration: underline;
}

/* Modal de Detalles */
.log-details {
  padding: 0;
}

.info-header {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  padding: 1.5rem;
  background: linear-gradient(
    135deg,
    var(--kl-surface-muted) 0%,
    var(--kl-border-soft) 100%
  );
  border-radius: var(--kl-radius-md);
  margin-bottom: 1.5rem;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.info-icon {
  font-size: 1.5rem;
  color: var(--kl-color-primary);
  flex-shrink: 0;
}

.info-label {
  display: block;
  font-size: var(--kl-fs-caption);
  color: var(--kl-text-muted);
  text-transform: uppercase;
  letter-spacing: var(--kl-ls-wide);
  font-weight: var(--kl-fw-semibold);
  margin-bottom: 0.25rem;
}

.info-value {
  margin: 0;
  font-size: var(--kl-fs-body);
  color: var(--kl-text-body);
  font-weight: var(--kl-fw-medium);
}

/* Comparación de Estados */
.states-comparison {
  margin-top: 1.5rem;
}

/* Tarjeta unificada */
.unified-card {
  background: var(--kl-surface);
  border-radius: var(--kl-radius-md);
  padding: 1.5rem;
  box-shadow: var(--kl-shadow-md);
  border: 1px solid var(--kl-border-soft);
}

.action-header {
  display: flex;
  align-items: center;
  padding: 1rem;
  margin: -1.5rem -1.5rem 1.5rem -1.5rem;
  border-radius: var(--kl-radius-md) var(--kl-radius-md) 0 0;
  font-weight: var(--kl-fw-semibold);
  color: var(--kl-text-on-primary);
}

.action-header h5 {
  margin: 0;
  font-size: var(--kl-fs-title-sm);
}

.create-header {
  background: var(--kl-color-success);
}

.delete-header {
  background: var(--kl-color-danger);
}

.field-row {
  border-bottom: 1px solid var(--kl-border-soft);
  transition: background-color 0.2s ease;
}

.field-row:last-child {
  border-bottom: none;
}

.field-row:hover {
  background-color: var(--kl-surface-muted);
}

.field-changed {
  background-color: #fff9e6;
  border-left: 3px solid var(--kl-color-warning);
  padding-left: calc(1rem - 3px);
  padding-bottom: 0.5rem;
}

.field-changed:hover {
  background-color: #fff4cc;
}

.field-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.field-label {
  display: flex;
  align-items: center;
  font-size: var(--kl-fs-body);
  color: var(--kl-text-body);
}

.change-icon {
  color: var(--kl-color-warning);
  font-size: 1rem;
}

.unchanged-icon {
  color: var(--kl-border-strong);
  font-size: 0.4rem;
}

.field-values {
  margin-top: 0.5rem;
}

.value-comparison {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.old-value,
.new-value {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
  min-width: 200px;
}

.value-label {
  font-size: var(--kl-fs-caption);
  font-weight: var(--kl-fw-semibold);
  text-transform: uppercase;
  letter-spacing: var(--kl-ls-wide);
}

.old-value .value-label {
  color: var(--kl-color-danger);
}

.new-value .value-label {
  color: var(--kl-color-success);
}

.arrow-icon {
  color: var(--kl-text-muted);
  font-size: 1.2rem;
  flex-shrink: 0;
  margin-top: 1rem;
}

.value-text {
  font-family: "Consolas", "Monaco", "Courier New", monospace;
  font-size: var(--kl-fs-small);
  color: var(--kl-text-body);
  white-space: pre-wrap;
  word-break: break-word;
  padding-left: 0.5rem;
  background: var(--kl-surface-muted);
  border-radius: var(--kl-radius-sm);
  display: block;
}

.old-value .value-text {
  background: #ffe6e6;
  border-left: 3px solid var(--kl-color-danger);
}

.new-value .value-text {
  background: #e6ffe6;
  border-left: 3px solid var(--kl-color-success);
}

.field-value-unchanged .value-text {
  background: var(--kl-surface-muted);
  border-left: 3px solid var(--kl-border-strong);
}

.field-value-single {
  margin-top: 0.5rem;
}

.field-value-single .value-text {
  background: var(--kl-surface-muted);
}

.html-content {
  font-family: inherit;
  font-size: var(--kl-fs-small);
}

.html-content >>> table {
  border-collapse: collapse;
  width: 100%;
  margin: 0.5rem 0;
}

.html-content >>> table td,
.html-content >>> table th {
  border: 1px solid var(--kl-border);
  padding: 0.35rem 0.5rem;
}

.html-content >>> p {
  margin-bottom: 0.25rem;
}

.deleted-value .value-text {
  background: #ffe6e6;
  text-decoration: line-through;
  opacity: 0.8;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: var(--kl-text-muted);
}

.empty-state .empty-icon {
  font-size: 3rem;
  color: var(--kl-border-strong);
  margin-bottom: 1rem;
}

.empty-state p {
  margin: 0;
  font-size: var(--kl-fs-body-lg);
  font-weight: var(--kl-fw-medium);
}

@media (max-width: 768px) {
  .logs-header {
    flex-direction: column;
  }

  .header-actions {
    margin-top: 1rem;
    width: 100%;
  }

  .filters-section {
    padding: 1rem;
  }

  .filters-grid {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .filter-group label {
    font-size: 0.8rem;
  }

  .input-with-icon,
  .select-with-icon {
    height: 38px;
    font-size: 0.9rem;
  }

  .comparison-grid {
    grid-template-columns: 1fr;
  }

  .info-header {
    grid-template-columns: 1fr;
  }
}
</style>
